// Imports
import React, { useRef, useState, PropsWithChildren, useCallback, useMemo, useEffect } from 'react';
import APIForm, { APIFormOptions } from '../../integrations/api/components/form';
import Input from '../../foundation/front-end/components/forms/fields/input';
import Select from '../../foundation/front-end/components/forms/fields/select';
import {
	faMapMarkerAlt,
	faGlobeAmericas,
	faCity,
	faHashtag,
	faTag,
	faPlus,
	faMapSigns,
	faMap,
} from '@fortawesome/pro-solid-svg-icons';
import Checkbox from '../../foundation/front-end/components/forms/fields/checkbox';
import { GetRegionsResponse } from '../../types/api/main/regions/get';
import { GetRegistrationResponse } from '../../types/api/main/website/settings/registration/get';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import get from 'lodash/get';
import { faUsps } from '@fortawesome/free-brands-svg-icons';
import MultiCheckboxContainer from '../../foundation/front-end/components/forms/multi-checkbox-container';
import styled from 'styled-components/macro';
import { fieldContainerBottomMargin } from '../../foundation/front-end/components/forms/field-container';
import StreetAddressInput from './street-address-input';
import ConditionalField from '../conditional-field';
import useCountryFunctionality from './utils/country/use-country-functionality';
import { z } from 'zod';
import Phone from '../../foundation/front-end/components/forms/fields/phone';


// Styled component
const Wrapper = styled.div`
	&:not(:last-child) {
		margin-bottom: ${fieldContainerBottomMargin};
	}
	
	> div {
		transition:
			margin-top 0.4s ease,
			height 0.4s ease !important;
		margin-top: 0;
		
		&.is-displayed ~ .is-displayed {
			margin-top: ${fieldContainerBottomMargin};
		}
	}
`;


// Define types
export interface Address {
	type: 'INTERNAL' | 'EXTERNAL';
	streetAddress: string;
	unitNumber: string | null;
	unitType: 'Unit' | 'Apt.' | 'Suite' | null;
	extraAddressDetail: string | null;
	alternateLocationID: number | null;
	division1: string | null;
	division2: string | null;
	regionID: number | null;
	postalCode: string | null;
	countryCode: string;
	billingAddress: boolean;
	mailingAddress: boolean;
	homePhone: string | null;
}


// Define field names for later use
const fieldNames: (keyof Address)[] = [
	'type',
	'streetAddress',
	'unitNumber',
	'unitType',
	'extraAddressDetail',
	'alternateLocationID',
	'division1',
	'division2',
	'regionID',
	'postalCode',
	'countryCode',
	'billingAddress',
	'mailingAddress',
	'homePhone',
];


// Helper function to recover address fields from the browser's persistent form state
const persistentFormSchema = z.object({
	state: z.object({
		persistentForm: z.record(z.record(z.string())),
	}),
});

const recoverPlaceFromHistoryState = (parentParameter?: string): Partial<Address> | null => {
	try {
		const historyState = persistentFormSchema.parse(window.history.state);
		const prefix = parentParameter ? `${parentParameter}.` : '';
		
		for (const formValues of Object.values(historyState.state.persistentForm)) {
			const recoveredPlace: Partial<Address> = {};
			let found = false;
			
			for (const [key, value] of Object.entries(formValues)) {
				const field = prefix
					? key.startsWith(prefix)
						? key.slice(prefix.length)
						: null
					: fieldNames.includes(key as keyof Address)
						? key
						: null;
				
				if (field) {
					(recoveredPlace as Record<string, string>)[field] = value;
					found = true;
				}
			}
			
			if (found) {
				return recoveredPlace;
			}
		}
	} catch {
		// Do nothing
	}
	
	return null;
};


// Define the accepted props
interface PropsBase<T> {
	initialPlace?: T;
	storybookRegions?: GetRegionsResponse;
	presentFields: (keyof Address)[];
	disabledFields?: (keyof Address)[];
	excludedFields?: (keyof Address)[];
	locations?: GetRegistrationResponse['locations'];
	extraAddressDetail?: GetRegistrationResponse['extraAddressDetail'];
	optionalUnitNumber?: boolean;
	hiddenUnitNumber?: boolean;
	fieldNotes?: { [K in Exclude<keyof Address, 'mailingAddress' | 'billingAddress'>]?: string | React.ReactElement };
	fieldErrors?: { [K in keyof Address]?: string };
	fallbackCountry?: GetRegistrationResponse['twoLetterCode'];
	parentParameter?: string;
	autoFocus?: boolean;
	displayPaymentProcessorSupportedRegionsOnly?: boolean;
	autocompleteDisabled?: true;
}

interface PropsInForm<T> extends PropsBase<T> {
	uri?: never;
	action?: never;
	onCompletion?: never;
	onCancel?: never;
	submitButtonText?: never;
	container?: never;
}

interface PropsStandaloneForm<T, S> extends PropsBase<T> {
	uri: string;
	action: string;
	onCompletion: React.ComponentProps<typeof APIForm<unknown, unknown, S>>['onCompletion'];
	onCancel?: () => void;
	submitButtonText?: string;
	container: React.FC<PropsWithChildren>;
}

type Props<T, S> = PropsInForm<T> | PropsStandaloneForm<T, S>;


// Function component
const PlaceUpserter = <T extends Partial<Address>, S>(props: PropsWithChildren<Props<T, S>>) => {
	// Deconstruct props
	const {
		uri,
		action,
		initialPlace,
		onCompletion,
		onCancel,
		locations,
		extraAddressDetail,
		presentFields,
		optionalUnitNumber,
		hiddenUnitNumber,
		disabledFields,
		excludedFields,
		fieldNotes,
		submitButtonText,
		fallbackCountry,
		parentParameter,
		container: Container,
		autoFocus,
		fieldErrors,
		displayPaymentProcessorSupportedRegionsOnly,
		autocompleteDisabled,
	} = props;
	
	
	// Use ref
	const regionSetter = useRef<(value: string | number) => void>();
	const postalCodeSetter = useRef<(value: string) => void>();
	const divisionOneSetter = useRef<(value: string) => void>();
	const divisionTwoSetter = useRef<(value: string) => void>();
	const alternateLocationSetter = useRef<(value: string) => void>();
	const suggestedInitialRegion = useRef<number>();
	const countryHasChanged = useRef(false);
	const hasInitializedLastCountryCode = useRef(false);
	
	
	// Use state
	const [place, setPlace] = useState<Partial<Address> | undefined>(() => {
		// Set to prop if given
		if (initialPlace) {
			return initialPlace;
		}
		
		
		// Set to history if possible
		const recovered = recoverPlaceFromHistoryState(parentParameter);
		
		if (recovered) {
			// If type wasn't persisted (user never changed it from the default),
			// apply the default so shouldDisplay can filter fields correctly
			if (!recovered.type && presentFields.includes('type')) {
				recovered.type = 'INTERNAL';
			}
			
			
			// Return
			return recovered;
		}
		
		
		// Handle all other cases
		return presentFields.includes('type') ? { type: 'INTERNAL' } : undefined;
	});
	
	
	// Reset stale state and sync place when initial place changes
	useEffect(() => {
		// Don't reset if history contains recovered values
		if (!initialPlace) {
			const recovered = recoverPlaceFromHistoryState(parentParameter);
			
			if (recovered) {
				return;
			}
		}
		
		
		// Reset
		suggestedInitialRegion.current = undefined;
		countryHasChanged.current = false;
		setPlace(initialPlace ?? (presentFields.includes('type') ? { type: 'INTERNAL' } : undefined));
	}, [initialPlace, parentParameter]); // eslint-disable-line react-hooks/exhaustive-deps
	
	
	// Function that decides if a field should be displayed
	const shouldDisplay = useCallback(
		(options: { name: keyof Address; labelAvailable?: boolean; fieldType?: Address['type'] }) => {
			// Deconstruct options
			const { name, labelAvailable, fieldType } = options;
			
			
			// If the field is not enabled then return false
			if (!presentFields.includes(name)) {
				return false;
			}
			
			
			// If type is provided in place, cross reference if it matches the type for this field
			if (fieldType && place?.type && place.type !== fieldType) {
				return false;
			}
			
			
			// If label availability is checked, and is not available return false
			if (labelAvailable !== undefined && !labelAvailable) {
				return false;
			}
			
			
			// Return true if none of the above conditions were met
			return true;
		},
		[presentFields, place?.type]
	);
	
	
	// Function that generates a field's name
	const generateFieldName = useCallback(
		(name: string) => `${parentParameter ? `${parentParameter}.` : ''}${name}`,
		[parentParameter]
	);
	
	
	// Use country functionality
	const { regions, countrySetter, lastCountryCode, countryDetails } = useCountryFunctionality<T>({
		place,
		shouldDisplay,
		initialPlace,
		fallbackCountry,
		displayPaymentProcessorSupportedRegionsOnly,
	});
	
	
	// Sync if place was recovered from history state so that the form change handler doesn't treat the recovered country as a change and clear region
	if (!hasInitializedLastCountryCode.current && place?.countryCode && !initialPlace) {
		lastCountryCode.current = place.countryCode;
		hasInitializedLastCountryCode.current = true;
	}
	
	
	// Once country details are loaded, set the initial region if it matches the selected country
	useEffect(() => {
		if (
			!countryHasChanged.current &&
			initialPlace?.regionID !== null &&
			Number.isInteger(initialPlace?.regionID) &&
			countryDetails?.code === initialPlace?.countryCode
		) {
			regionSetter.current?.(Number(initialPlace?.regionID));
		}
	}, [countryDetails?.code, initialPlace?.countryCode, initialPlace?.regionID]);
	
	
	// Update last country code in case initial place loads asynchronously
	useEffect(() => {
		// Skip if we already initialized from recovered history state
		if (hasInitializedLastCountryCode.current) {
			return;
		}
		
		
		// Sync
		lastCountryCode.current = initialPlace?.countryCode;
	}, [initialPlace?.countryCode, lastCountryCode]);
	
	
	// Decide which field to autofocus
	const autoFocusField = useMemo(() => {
		if (!autoFocus) {
			return null;
		}
		
		const focusableMap: { [name in Exclude<keyof Address, 'unitType'>]: boolean } = {
			type: !disabledFields?.includes('type') && shouldDisplay({ name: 'type' }),
			countryCode:
				!disabledFields?.includes('countryCode') && shouldDisplay({ name: 'countryCode', fieldType: 'EXTERNAL' }),
			streetAddress: !disabledFields?.includes('streetAddress') && shouldDisplay({ name: 'streetAddress' }),
			unitNumber:
				!disabledFields?.includes('unitNumber') &&
				shouldDisplay({ name: 'unitNumber' }) &&
				!(hiddenUnitNumber && place?.type === 'INTERNAL'),
			extraAddressDetail:
				!disabledFields?.includes('extraAddressDetail') &&
				shouldDisplay({ name: 'extraAddressDetail', fieldType: 'INTERNAL' }),
			alternateLocationID:
				!disabledFields?.includes('alternateLocationID') &&
				shouldDisplay({ name: 'alternateLocationID', fieldType: 'INTERNAL' }),
			division2:
				!disabledFields?.includes('division2') &&
				shouldDisplay({
					name: 'division2',
					labelAvailable: Boolean(countryDetails?.divisionNames?.['2']),
					fieldType: 'EXTERNAL',
				}),
			division1:
				!disabledFields?.includes('division1') &&
				shouldDisplay({
					name: 'division1',
					labelAvailable: Boolean(countryDetails?.divisionNames?.['1']),
					fieldType: 'EXTERNAL',
				}),
			regionID:
				!disabledFields?.includes('regionID') &&
				shouldDisplay({
					name: 'regionID',
					labelAvailable: Boolean(countryDetails?.regionChoiceLabel),
					fieldType: 'EXTERNAL',
				}),
			postalCode:
				!disabledFields?.includes('postalCode') &&
				shouldDisplay({
					name: 'postalCode',
					labelAvailable: Boolean(countryDetails?.nameForPostalCode),
					fieldType: 'EXTERNAL',
				}),
			homePhone: !disabledFields?.includes('homePhone') && shouldDisplay({ name: 'homePhone' }),
			mailingAddress: !disabledFields?.includes('mailingAddress') && shouldDisplay({ name: 'mailingAddress' }),
			billingAddress: !disabledFields?.includes('billingAddress') && shouldDisplay({ name: 'billingAddress' }),
		};
		
		return Object.keys(focusableMap).find((key) => focusableMap[key as keyof typeof focusableMap]) ?? null;
	}, [autoFocus]); // eslint-disable-line react-hooks/exhaustive-deps
	
	
	// Throw if fallback country should be present
	if (
		(presentFields.includes('type') &&
			!(disabledFields?.includes('type') && initialPlace?.type === 'EXTERNAL') &&
			!fallbackCountry) ||
		(presentFields.includes('regionID') && !presentFields.includes('countryCode') && !fallbackCountry)
	) {
		throw new Error('Fallback country should be present');
	}
	
	
	// Type guard to check if our parameters have the shape of an address
	const isAddress = useCallback(
		(place: unknown): place is T =>
			typeof place === 'object' &&
			place !== null &&
			Object.keys(place).every((key) => fieldNames.some((name) => name === key)),
		[]
	);
	
	
	// Handle form change
	const handleFormChange = useCallback(
		(values: unknown) => {
			// Extract address
			const extractedValues: unknown = parentParameter ? get(values, parentParameter) : values;
			
			
			// If values are not a subset of the address type, do nothing
			if (!isAddress(extractedValues)) {
				throw new Error(`Form values did not form a valid address: ${JSON.stringify(extractedValues)}`);
			}
			
			
			// If the country changes, clear the region (all new choices will be presented)
			if (extractedValues.countryCode && extractedValues.countryCode !== lastCountryCode.current) {
				regionSetter.current?.('');
				
				lastCountryCode.current = extractedValues.countryCode;
				
				countryHasChanged.current = true;
			}
			
			
			// If current form values do not match up with state, replace state
			if (!isEqual(place, extractedValues)) {
				setPlace(extractedValues);
			}
		},
		[place, parentParameter, isAddress, lastCountryCode]
	);
	
	
	// Handle field change
	const handleFieldChange = useCallback(
		(value: React.ReactText | boolean, name: (typeof fieldNames)[0]) => {
			// Build values
			let values: Partial<Address> = {};
			const clonedPlace = cloneDeep(place ?? {});
			
			if (parentParameter) {
				set(values, parentParameter, clonedPlace);
			} else {
				values = clonedPlace;
			}
			
			
			// Set value at path
			set<Partial<Address>>(values, generateFieldName(name), value);
			
			
			// Handle as form values
			handleFormChange(values);
		},
		[place, parentParameter, generateFieldName, handleFormChange]
	);
	
	
	// Handle place changed
	const handlePlaceChanged: React.ComponentProps<typeof StreetAddressInput>['onPlaceChanged'] = useCallback(
		(parsedResults, alternateLocationChange) => {
			// Set fields based on parsed results
			if (parsedResults.countryCode) {
				suggestedInitialRegion.current = parsedResults.regionID;
				
				countrySetter.current?.(parsedResults.countryCode);
			}
			
			if (parsedResults.regionID) {
				regionSetter.current?.(parsedResults.regionID);
			}
			
			if (parsedResults.postalCode) {
				postalCodeSetter.current?.(parsedResults.postalCode);
			}
			
			if (parsedResults.locality) {
				const country = regions?.find((region) => region.code === parsedResults.countryCode);
				
				if (country?.divisionNames?.['1']) {
					divisionOneSetter.current?.(parsedResults.locality);
				}
				
				if (!country?.divisionNames?.['1'] && country?.divisionNames?.['2']) {
					divisionTwoSetter.current?.(parsedResults.locality);
				}
			}
			
			
			// Set location based on autocomplete selection
			if (alternateLocationChange.type === 'UPDATE' && (locations?.length ?? 0) > 1) {
				alternateLocationSetter.current?.(alternateLocationChange.locationID?.toString() ?? '');
			}
		},
		[regions, countrySetter, locations?.length]
	);
	
	
	// Build form options
	const formOptions: APIFormOptions<Partial<Address>, undefined, S> | null =
		uri !== undefined && action !== undefined
			? {
					action: action,
					uri: uri,
					submitButton: {
						text: submitButtonText ?? (initialPlace ? 'Save' : 'Add'),
					},
					recoverState: initialPlace === undefined,
				}
			: null;
	
	
	// Build fields
	const isForm = 'uri' in props;
	
	const fields = useMemo(
		() => (
			<Wrapper>
				<ConditionalField isDisplayed={shouldDisplay({ name: 'type' })}>
					<Select
						name={generateFieldName('type')}
						label='This address is…'
						icon={faMapMarkerAlt}
						initialValue={initialPlace?.type}
						note={fieldNotes?.type}
						exclude={excludedFields?.includes('type') || !shouldDisplay({ name: 'type' })}
						disabled={disabledFields?.includes('type') || !shouldDisplay({ name: 'type' })}
						onChange={
							isForm || !shouldDisplay({ name: 'type' }) ? undefined : (value) => handleFieldChange(value, 'type')
						}
						autoFocus={autoFocusField === 'type' ? true : undefined}
						errorMessage={fieldErrors?.type}
					>
						<option value='INTERNAL'>
							Inside your community{locations?.length === 1 && ` (${locations[0].formatted})`}
						</option>
						<option value='EXTERNAL'>Outside your community</option>
					</Select>
				</ConditionalField>
				
				<ConditionalField isDisplayed={shouldDisplay({ name: 'countryCode', fieldType: 'EXTERNAL' })}>
					<Select
						name={generateFieldName('countryCode')}
						label='Country'
						icon={faGlobeAmericas}
						initialValue={initialPlace?.countryCode}
						placeholder='Please select…'
						note={fieldNotes?.countryCode}
						exclude={
							excludedFields?.includes('countryCode') || !shouldDisplay({ name: 'countryCode', fieldType: 'EXTERNAL' })
						}
						bindSetter={(setter) => (countrySetter.current = setter)}
						disabled={
							disabledFields?.includes('countryCode') || !shouldDisplay({ name: 'countryCode', fieldType: 'EXTERNAL' })
						}
						onChange={
							isForm || !shouldDisplay({ name: 'countryCode', fieldType: 'EXTERNAL' })
								? undefined
								: (value) => handleFieldChange(value, 'countryCode')
						}
						autoFocus={autoFocusField === 'countryCode' ? true : undefined}
						errorMessage={fieldErrors?.countryCode}
						required={
							!excludedFields?.includes('countryCode') && !shouldDisplay({ name: 'countryCode', fieldType: 'EXTERNAL' })
						}
					>
						{regions?.map((country) => (
							<option key={country.code} value={country.code}>
								{country.name}
							</option>
						))}
					</Select>
				</ConditionalField>
				<ConditionalField isDisplayed={shouldDisplay({ name: 'streetAddress' })}>
					<StreetAddressInput
						name={generateFieldName('streetAddress')}
						initialValue={initialPlace?.streetAddress}
						note={fieldNotes?.streetAddress}
						exclude={excludedFields?.includes('streetAddress') || !shouldDisplay({ name: 'streetAddress' })}
						disabled={disabledFields?.includes('streetAddress') || !shouldDisplay({ name: 'streetAddress' })}
						onChange={
							isForm || !shouldDisplay({ name: 'streetAddress' })
								? undefined
								: (value) => handleFieldChange(value, 'streetAddress')
						}
						autoFocus={autoFocusField === 'streetAddress' ? true : undefined}
						errorMessage={fieldErrors?.streetAddress}
						threeLetterCountryCode={place?.countryCode}
						fallbackCountry={fallbackCountry}
						type={place?.type}
						regions={regions}
						onPlaceChanged={handlePlaceChanged}
						required={!excludedFields?.includes('streetAddress') && shouldDisplay({ name: 'streetAddress' })}
						autocompleteDisabled={autocompleteDisabled}
					/>
				</ConditionalField>
				
				<ConditionalField
					isDisplayed={shouldDisplay({ name: 'unitNumber' }) && !(hiddenUnitNumber && place?.type === 'INTERNAL')}
				>
					<Input
						type='text'
						name={generateFieldName('unitNumber')}
						label='Unit number'
						icon={faHashtag}
						initialValue={initialPlace?.unitNumber ?? undefined}
						note={fieldNotes?.unitNumber}
						required={
							shouldDisplay({ name: 'unitNumber', fieldType: 'INTERNAL' }) &&
							!(hiddenUnitNumber && place?.type === 'INTERNAL') &&
							!optionalUnitNumber
						}
						exclude={
							excludedFields?.includes('unitNumber') ||
							!shouldDisplay({ name: 'unitNumber' }) ||
							(hiddenUnitNumber && place?.type === 'INTERNAL')
						}
						maxLength={10}
						disabled={
							disabledFields?.includes('unitNumber') ||
							!shouldDisplay({ name: 'unitNumber' }) ||
							(hiddenUnitNumber && place?.type === 'INTERNAL')
						}
						onChange={
							isForm || !shouldDisplay({ name: 'unitNumber' }) || (hiddenUnitNumber && place?.type === 'INTERNAL')
								? undefined
								: (value) => handleFieldChange(value, 'unitNumber')
						}
						autoFocus={autoFocusField === 'unitNumber' ? true : undefined}
						errorMessage={fieldErrors?.unitNumber}
					/>
				</ConditionalField>
				
				<ConditionalField isDisplayed={Boolean(place?.unitNumber) && !(hiddenUnitNumber && place?.type === 'INTERNAL')}>
					<Select
						name={generateFieldName('unitType')}
						label='Unit type'
						icon={faTag}
						initialValue={initialPlace?.unitType ?? undefined}
						note={fieldNotes?.unitType}
						exclude={excludedFields?.includes('unitType') || !place?.unitNumber}
						required={
							Boolean(place?.unitNumber) &&
							shouldDisplay({ name: 'unitNumber', fieldType: 'INTERNAL' }) &&
							!optionalUnitNumber &&
							!(hiddenUnitNumber && place?.type === 'INTERNAL')
						}
						disabled={disabledFields?.includes('unitType') || !place?.unitNumber}
						onChange={isForm || !place?.unitNumber ? undefined : (value) => handleFieldChange(value, 'unitType')}
						errorMessage={fieldErrors?.unitType}
					>
						<option>Unit</option>
						<option>Apt.</option>
						<option>Suite</option>
					</Select>
				</ConditionalField>
				
				<ConditionalField isDisplayed={shouldDisplay({ name: 'extraAddressDetail', fieldType: 'INTERNAL' })}>
					<Input
						type='text'
						name={generateFieldName('extraAddressDetail')}
						label={extraAddressDetail ?? 'Extra address detail'}
						icon={faPlus}
						initialValue={initialPlace?.extraAddressDetail ?? undefined}
						note={fieldNotes?.extraAddressDetail}
						maxLength={25}
						exclude={
							excludedFields?.includes('extraAddressDetail') ||
							!shouldDisplay({ name: 'extraAddressDetail', fieldType: 'INTERNAL' })
						}
						disabled={
							disabledFields?.includes('extraAddressDetail') ||
							!shouldDisplay({ name: 'extraAddressDetail', fieldType: 'INTERNAL' })
						}
						onChange={
							isForm || !shouldDisplay({ name: 'extraAddressDetail', fieldType: 'INTERNAL' })
								? undefined
								: (value) => handleFieldChange(value, 'extraAddressDetail')
						}
						autoFocus={autoFocusField === 'extraAddressDetail' ? true : undefined}
						errorMessage={fieldErrors?.extraAddressDetail}
						required={
							shouldDisplay({ name: 'extraAddressDetail', fieldType: 'INTERNAL' }) &&
							!excludedFields?.includes('extraAddressDetail')
						}
					/>
				</ConditionalField>
				
				<ConditionalField isDisplayed={shouldDisplay({ name: 'alternateLocationID', fieldType: 'INTERNAL' })}>
					<Select
						name={generateFieldName('alternateLocationID')}
						label='Location'
						icon={faMapMarkerAlt}
						initialValue={initialPlace?.alternateLocationID ?? undefined}
						note={fieldNotes?.alternateLocationID}
						bindSetter={(setter) => (alternateLocationSetter.current = setter)}
						exclude={
							excludedFields?.includes('alternateLocationID') ||
							!shouldDisplay({ name: 'alternateLocationID', fieldType: 'INTERNAL' })
						}
						disabled={
							disabledFields?.includes('alternateLocationID') ||
							!shouldDisplay({ name: 'alternateLocationID', fieldType: 'INTERNAL' })
						}
						onChange={
							isForm || !shouldDisplay({ name: 'alternateLocationID', fieldType: 'INTERNAL' })
								? undefined
								: (value) => handleFieldChange(value, 'alternateLocationID')
						}
						autoFocus={autoFocusField === 'alternateLocationID' ? true : undefined}
						errorMessage={fieldErrors?.alternateLocationID}
					>
						{locations?.map((location) => (
							<option key={location.id || 'default-location'} value={location.id || ''}>
								{location.formatted}
							</option>
						))}
					</Select>
				</ConditionalField>
				
				<ConditionalField
					isDisplayed={shouldDisplay({
						name: 'division2',
						labelAvailable: Boolean(countryDetails?.divisionNames?.['2']),
						fieldType: 'EXTERNAL',
					})}
				>
					<Input
						type='text'
						name={generateFieldName('division2')}
						label={countryDetails?.divisionNames?.['2'] ?? 'Division 2'}
						icon={faMapSigns}
						initialValue={initialPlace?.division2 ?? undefined}
						note={fieldNotes?.division2}
						prefix={countryDetails?.divisionPrefixes?.['2'] ?? undefined}
						maxLength={40}
						exclude={
							excludedFields?.includes('division2') ||
							!shouldDisplay({
								name: 'division2',
								labelAvailable: Boolean(countryDetails?.divisionNames?.['2']),
								fieldType: 'EXTERNAL',
							})
						}
						disabled={
							disabledFields?.includes('division2') ||
							!shouldDisplay({
								name: 'division2',
								labelAvailable: Boolean(countryDetails?.divisionNames?.['2']),
								fieldType: 'EXTERNAL',
							})
						}
						bindSetter={(setter) => (divisionTwoSetter.current = setter)}
						onChange={
							isForm ||
							!shouldDisplay({
								name: 'division2',
								labelAvailable: Boolean(countryDetails?.divisionNames?.['2']),
								fieldType: 'EXTERNAL',
							})
								? undefined
								: (value) => handleFieldChange(value, 'division2')
						}
						autoFocus={autoFocusField === 'division2' ? true : undefined}
						errorMessage={fieldErrors?.division2}
						required={
							!excludedFields?.includes('division2') &&
							shouldDisplay({
								name: 'division2',
								labelAvailable: Boolean(countryDetails?.divisionNames?.['2']),
								fieldType: 'EXTERNAL',
							})
						}
					/>
				</ConditionalField>
				
				<ConditionalField
					isDisplayed={shouldDisplay({
						name: 'division1',
						labelAvailable: Boolean(countryDetails?.divisionNames?.['1']),
						fieldType: 'EXTERNAL',
					})}
				>
					<Input
						type='text'
						name={generateFieldName('division1')}
						label={countryDetails?.divisionNames?.['1'] ?? 'Division 1'}
						icon={faCity}
						initialValue={initialPlace?.division1 || undefined}
						note={fieldNotes?.division1}
						prefix={countryDetails?.divisionPrefixes?.['1'] ?? undefined}
						maxLength={40}
						exclude={
							excludedFields?.includes('division1') ||
							!shouldDisplay({
								name: 'division1',
								labelAvailable: Boolean(countryDetails?.divisionNames?.['1']),
								fieldType: 'EXTERNAL',
							})
						}
						disabled={
							disabledFields?.includes('division1') ||
							!shouldDisplay({
								name: 'division1',
								labelAvailable: Boolean(countryDetails?.divisionNames?.['1']),
								fieldType: 'EXTERNAL',
							})
						}
						bindSetter={(setter) => (divisionOneSetter.current = setter)}
						onChange={
							isForm ||
							!shouldDisplay({
								name: 'division1',
								labelAvailable: Boolean(countryDetails?.divisionNames?.['1']),
								fieldType: 'EXTERNAL',
							})
								? undefined
								: (value) => handleFieldChange(value, 'division1')
						}
						autoFocus={autoFocusField === 'division1' ? true : undefined}
						errorMessage={fieldErrors?.division1}
						required={
							!excludedFields?.includes('division1') &&
							shouldDisplay({
								name: 'division1',
								labelAvailable: Boolean(countryDetails?.divisionNames?.['1']),
								fieldType: 'EXTERNAL',
							})
						}
					/>
				</ConditionalField>
				
				<ConditionalField
					isDisplayed={shouldDisplay({
						name: 'regionID',
						labelAvailable: Boolean(countryDetails?.regionChoiceLabel),
						fieldType: 'EXTERNAL',
					})}
				>
					{countryDetails && (
						<Select
							name={generateFieldName('regionID')}
							label={countryDetails?.regionChoiceLabel ?? 'Region'}
							icon={faMap}
							initialValue={
								initialPlace?.regionID !== null &&
								Number.isInteger(initialPlace?.regionID) &&
								(countryDetails.code === initialPlace?.countryCode ||
									(!initialPlace?.countryCode && fallbackCountry && countryDetails.twoLetterCode === fallbackCountry))
									? initialPlace?.regionID
									: suggestedInitialRegion.current
							}
							bindSetter={(setter) => (regionSetter.current = setter)}
							placeholder='Please select…'
							note={fieldNotes?.regionID}
							exclude={
								excludedFields?.includes('regionID') ||
								!shouldDisplay({
									name: 'regionID',
									labelAvailable: Boolean(countryDetails?.regionChoiceLabel),
									fieldType: 'EXTERNAL',
								})
							}
							disabled={
								disabledFields?.includes('regionID') ||
								!shouldDisplay({
									name: 'regionID',
									labelAvailable: Boolean(countryDetails?.regionChoiceLabel),
									fieldType: 'EXTERNAL',
								})
							}
							onChange={
								isForm ||
								!shouldDisplay({
									name: 'regionID',
									labelAvailable: Boolean(countryDetails?.regionChoiceLabel),
									fieldType: 'EXTERNAL',
								})
									? undefined
									: (value) => handleFieldChange(value, 'regionID')
							}
							autoFocus={autoFocusField === 'regionID' ? true : undefined}
							errorMessage={fieldErrors?.regionID}
							required={
								!excludedFields?.includes('regionID') &&
								shouldDisplay({
									name: 'regionID',
									labelAvailable: Boolean(countryDetails?.regionChoiceLabel),
									fieldType: 'EXTERNAL',
								})
							}
						>
							{countryDetails?.regions &&
								Object.entries(countryDetails.regions).map(([category, regions], _, allCategories) => {
									const options = regions.map((region) => (
										<option key={region.id} value={region.id}>
											{region.name}
										</option>
									));
									
									if (allCategories.length === 1) {
										return options;
									}
									
									return (
										<optgroup key={category} label={category}>
											{options}
										</optgroup>
									);
								})}
						</Select>
					)}
				</ConditionalField>
				
				<ConditionalField
					isDisplayed={shouldDisplay({
						name: 'postalCode',
						labelAvailable: Boolean(countryDetails?.nameForPostalCode),
						fieldType: 'EXTERNAL',
					})}
				>
					<Input
						type='text'
						name={generateFieldName('postalCode')}
						label={countryDetails?.nameForPostalCode ?? 'Postal code'}
						icon={place?.countryCode === 'USA' ? faUsps : faMapMarkerAlt}
						initialValue={initialPlace?.postalCode || undefined}
						note={fieldNotes?.postalCode}
						exclude={
							excludedFields?.includes('postalCode') ||
							!shouldDisplay({
								name: 'postalCode',
								labelAvailable: Boolean(countryDetails?.nameForPostalCode),
								fieldType: 'EXTERNAL',
							})
						}
						disabled={
							disabledFields?.includes('postalCode') ||
							!shouldDisplay({
								name: 'postalCode',
								labelAvailable: Boolean(countryDetails?.nameForPostalCode),
								fieldType: 'EXTERNAL',
							})
						}
						onChange={
							isForm ||
							!shouldDisplay({
								name: 'postalCode',
								labelAvailable: Boolean(countryDetails?.nameForPostalCode),
								fieldType: 'EXTERNAL',
							})
								? undefined
								: (value) => handleFieldChange(value, 'postalCode')
						}
						bindSetter={(setter) => (postalCodeSetter.current = setter)}
						autoFocus={autoFocusField === 'postalCode' ? true : undefined}
						errorMessage={fieldErrors?.postalCode}
					/>
				</ConditionalField>
				
				<ConditionalField isDisplayed={shouldDisplay({ name: 'homePhone' })}>
					<Phone
						name={generateFieldName('homePhone')}
						label='Home phone'
						initialValue={initialPlace?.homePhone ?? undefined}
						maxLength={20}
						note={fieldNotes?.homePhone}
						exclude={excludedFields?.includes('homePhone') || !shouldDisplay({ name: 'homePhone' })}
						disabled={disabledFields?.includes('homePhone') || !shouldDisplay({ name: 'homePhone' })}
						onChange={isForm || !place?.homePhone ? undefined : (value) => handleFieldChange(value, 'homePhone')}
						errorMessage={fieldErrors?.homePhone}
					/>
				</ConditionalField>
				
				<ConditionalField isDisplayed={shouldDisplay({ name: 'mailingAddress' })}>
					<MultiCheckboxContainer>
						<Checkbox
							name={generateFieldName('mailingAddress')}
							label={
								initialPlace?.mailingAddress ? 'Preferred mailing address' : 'Make this the preferred mailing address'
							}
							initiallyChecked={initialPlace?.mailingAddress}
							disabled={
								initialPlace?.mailingAddress ||
								disabledFields?.includes('mailingAddress') ||
								!shouldDisplay({ name: 'mailingAddress' })
							}
							exclude={excludedFields?.includes('mailingAddress') || !shouldDisplay({ name: 'mailingAddress' })}
							onChange={
								isForm || !shouldDisplay({ name: 'mailingAddress' })
									? undefined
									: (value) => handleFieldChange(value, 'mailingAddress')
							}
							autoFocus={autoFocusField === 'mailingAddress' ? true : undefined}
							errorMessage={fieldErrors?.mailingAddress}
						/>
						
						{shouldDisplay({ name: 'billingAddress' }) && (
							<Checkbox
								name={generateFieldName('billingAddress')}
								label={
									initialPlace?.billingAddress ? 'Preferred billing address' : 'Make this the preferred billing address'
								}
								initiallyChecked={initialPlace?.billingAddress}
								disabled={initialPlace?.billingAddress || disabledFields?.includes('billingAddress')}
								exclude={excludedFields?.includes('billingAddress')}
								onChange={isForm ? undefined : (value) => handleFieldChange(value, 'billingAddress')}
								autoFocus={autoFocusField === 'billingAddress' ? true : undefined}
								errorMessage={fieldErrors?.billingAddress}
							/>
						)}
					</MultiCheckboxContainer>
				</ConditionalField>
			</Wrapper>
		),
		[
			shouldDisplay,
			generateFieldName,
			countryDetails,
			initialPlace,
			excludedFields,
			disabledFields,
			isForm,
			autoFocusField,
			fieldErrors,
			handleFieldChange,
			extraAddressDetail,
			fieldNotes,
			hiddenUnitNumber,
			locations,
			optionalUnitNumber,
			place,
			regions,
			fallbackCountry,
			handlePlaceChanged,
			countrySetter,
			autocompleteDisabled,
		]
	);
	
	
	// If loading, then display nothing
	if (!regions) {
		return null;
	}
	
	
	// Return JSX
	return formOptions ? (
		<APIForm options={formOptions} onCompletion={onCompletion} onCancel={onCancel} onChange={handleFormChange}>
			{Container && <Container>{fields}</Container>}
		</APIForm>
	) : (
		fields
	);
};

export default PlaceUpserter;
